/**
 * pitch-tracker.mock.ts — in-memory mock of the native PitchTracker module.
 *
 * A deterministic, JS-only stand-in for the real TurboModule so the WebView /
 * mobile bridge (#10) and UI can be built before native exists. It emits a synthetic
 * sustained voice as `pitch_frame` events — NEVER raw PCM (contract invariant).
 *
 * Determinism: each frame's pitch is a pure function of its index; no randomness.
 * Each `start()` resets the index so identical inputs yield identical event streams.
 *
 * Config lives in `pitch-tracker.config.ts` and is CONSUMED here, not defined here:
 * when the mock owned it, mock-convenient values (a -60 dBFS silence gate) rode all
 * the way to the device, and `eventIntervalMs` — which only the mock honours — looked
 * like a knob that worked everywhere.
 */
import {
  createPitchTrackerConfig,
  effectiveEventIntervalMs,
} from './pitch-tracker.config';

import type {
  DiagnosticsSnapshot,
  NativeF0Frame,
  PitchAlgorithm,
  PitchTrackerConfig,
  PitchTrackerEvent,
  PitchTrackerEventName,
  PitchTrackerEventOf,
  PitchTrackerModule,
  PitchTrackerSessionInfo,
  RealtimePerformanceMetrics,
  Subscription,
} from './pitch-tracker';

const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

/**
 * Synthetic voice: a **sustained** A3 with a shallow 2 Hz vibrato (±20 cents).
 *
 * It used to be a melody (`[60,62,…,72]`, one entry per frame) which swept a whole
 * octave every ~170 ms. Nothing human does that, and it had a hard consequence: the
 * onboarding diagnosis rejects a sustain clip with no dominant held pitch
 * (`isValidVoice`: the frames within half an octave of the median must be a
 * majority, and their cent-std must stay under 200) — a full-octave sweep has none,
 * so the browser flow could never reach its result screen. A held tone with a small
 * wobble is both what the `/a/` test actually asks for and still visibly alive on
 * the live graph. Deterministic: the wobble is a function of the frame index.
 */
const SUSTAIN_MIDI = 57; // A3 — voice-engine's "comfortable for every gender" anchor
const VIBRATO_CENTS = 20;
const VIBRATO_PERIOD_FRAMES = 24; // ≈2 Hz at the native ~47 fps frame rate

/**
 * What the synthetic voice sings. `'sustain'` (default) holds one note — that is what
 * the diagnosis asks for. `'melody'` walks a C-major scale for the scoring demo, which
 * needs the pitch to actually move between target notes.
 */
/**
 * 합성 세션의 식별자. **소비처가 "이 회차가 실측인가"를 판정하는 근거**라 상수로 낸다.
 *
 * 프레임의 `algorithm`으로는 못 가른다: mock은 config가 고른 값(기본 `'mpm'`)을 그대로
 * 실어서 네이티브 MPM 프레임과 구분되지 않는다. 세션 식별자는 모듈이 자기 이름을 대는
 * 자리라 그 역할을 할 수 있다.
 */
export const MOCK_SESSION_ID = 'mock-session';

export type MockVoice = 'sustain' | 'melody';

const MELODY_MIDIS = [60, 62, 64, 65, 67, 69, 71, 72];
/** ≈1s per note at the native ~47 fps. One note per *frame* is unscorable — no note is
 *  held long enough to be judged, which is what the old mock did. */
const MELODY_FRAMES_PER_NOTE = 47;

const wobble = (index: number): number =>
  (VIBRATO_CENTS / 100) *
  Math.sin((2 * Math.PI * index) / VIBRATO_PERIOD_FRAMES);

/**
 * 소리를 내기 전의 조용한 도입부(프레임 수). ≈0.4초.
 *
 * mock이 첫 프레임부터 끝까지 **같은 레벨**이면 레벨을 읽는 소비처를 아무것도 검증하지
 * 못한다. 특히 상대 게이트(`voice-gate`)는 "이 방의 조용함 대비"로 판정하므로, 변화가
 * 없는 스트림에서는 기준을 잡을 수 없어 브라우저 개발 경로가 통째로 무성이 된다.
 * 실제 테이크도 카운트다운 직후 잠깐은 조용하다 — 그 모양을 따른다.
 */
const LEAD_IN_FRAMES = 20;
/** 도입부의 암소음 레벨(dBFS). 실기기 계측 범위(-44 ~ -47)와 같은 자리. */
const AMBIENT_DB = -45;
/** 발성 레벨(dBFS). */
const VOICE_DB = -18;

const mockMidi = (index: number, voice: MockVoice): number => {
  if (voice === 'melody') {
    const step = Math.floor(index / MELODY_FRAMES_PER_NOTE);
    return MELODY_MIDIS[step % MELODY_MIDIS.length] + wobble(index);
  }
  return SUSTAIN_MIDI + wobble(index);
};

const midiToFreq = (midi: number): number =>
  440 * Math.pow(2, (midi - 69) / 12);

/** 반올림하고 이름을 찾는다 — midi는 비브라토 때문에 정수가 아니다(소수를 그대로
 *  인덱스로 쓰면 `NOTE_NAMES[6.85]`가 undefined라 "undefined4" 같은 노트가 나온다). */
const midiToNote = (midi: number): string => {
  const rounded = Math.round(midi);
  const name = NOTE_NAMES[((rounded % 12) + 12) % 12];
  const octave = Math.floor(rounded / 12) - 1;
  return `${name}${octave}`;
};

const buildFrame = (
  config: PitchTrackerConfig,
  index: number,
  atMs: number,
  voice: MockVoice,
): NativeF0Frame => {
  const silent = index < LEAD_IN_FRAMES;
  const midi = mockMidi(index - LEAD_IN_FRAMES, voice);
  const captureSampleRate = config.audio.captureSampleRate;
  return {
    timestampMs: atMs,
    f0Hz: silent ? null : midiToFreq(midi),
    midi: silent ? null : midi,
    note: silent ? null : midiToNote(midi),
    confidence: silent ? 0 : 0.95,
    voiced: !silent,
    rmsDb: silent ? AMBIENT_DB : VOICE_DB,
    algorithm: config.analysis.algorithm as PitchAlgorithm,
    captureSampleRate,
    analysisSampleRate: captureSampleRate,
  };
};

/** Frames per metrics emission — synthetic `performance_metrics` cadence (≈1Hz). */
const METRICS_EVERY_FRAMES = 20;

/**
 * Synthetic, deterministic `performance_metrics`. Values sit comfortably inside the
 * frame budget (P95 < 21ms) with no overruns/drops, so the dev perf panel renders a
 * passing state in mock mode. Native emits the real measurements.
 */
const buildMetrics = (
  index: number,
  atMs: number,
): RealtimePerformanceMetrics => {
  const avgMs = 8 + (index % 8) * 0.25;
  return {
    timestampMs: atMs,
    audioCallbackAvgMs: 1.2,
    audioCallbackMaxMs: 3.4,
    ringBufferBacklogMs: 0,
    ringBufferOverrunCount: 0,
    inputDropoutCount: 0,
    timestampDiscontinuityCount: 0,
    analysisAvgMs: avgMs,
    analysisP95Ms: avgMs + 4,
    analysisDeadlineMissCount: 0,
    eventQueueDepth: 1,
    jsEventDroppedCount: 0,
    pitchFrameEmittedCount: index,
    pitchFrameDroppedCount: 0,
    uiHeldFrameCount: 0,
    uiInterpolatedFrameCount: 0,
    cpuUsageEstimate: 0.35,
    thermalState: 'nominal',
  };
};

/** mock이 **실제로 아는** 상태. 모르는 값(오디오 콜백·링버퍼)은 여기 없다. */
interface MockRuntime {
  running: boolean;
  startedAtMs: number | null;
  frameIndex: number;
  lastFrameAtMs: number | null;
}

/**
 * 스냅샷은 mock이 실제로 아는 것만 말한다(티켓 12의 네이티브 쪽과 같은 규칙).
 * 전에는 `state: 'running'`·`uptimeMs: 0`·`frameCount: 0`이 세션 상태와 무관하게 박혀
 * 있어서, 정지 중에도 "실행 중"이라 답하고 몇 분을 돌려도 프레임 0개라고 말했다.
 * 오디오 콜백·링버퍼 수치는 mock에 대응물이 아예 없어 0으로 남긴다 — 그건 미측정이지
 * 낙관값이 아니다(룰 엔진의 unknown 처리와 별개로 mock이 지어내지 않는다).
 */
const buildDiagnostics = (
  config: PitchTrackerConfig,
  atMs: number,
  runtime: MockRuntime,
): DiagnosticsSnapshot => ({
  schemaVersion: 1,
  timestampMs: atMs,
  session: {
    sessionId: MOCK_SESSION_ID,
    state: runtime.running ? 'running' : 'idle',
    startedAtMs: runtime.startedAtMs,
    uptimeMs: runtime.startedAtMs === null ? 0 : atMs - runtime.startedAtMs,
    configVersion: config.version,
    engineVersion: 'mock-0.0.0',
  },
  audioInput: {
    backend: 'unknown',
    permissionState: 'granted',
    actualSampleRate: config.audio.captureSampleRate,
    channelCount: config.audio.channelCount,
    inputBufferSizeFrames: config.analysis.hopSize,
    callbackCount: 0,
    callbackAvgMs: null,
    callbackMaxMs: null,
    callbackOverBudgetCount: 0,
    inputDropoutCount: 0,
    lastCallbackAgeMs: null,
  },
  ringBuffer: {
    capacityMs: 0,
    backlogMs: 0,
    fillRatio: 0,
    writeCount: 0,
    readCount: 0,
    overrunCount: 0,
    underrunCount: 0,
    droppedInputFrames: 0,
    droppedAnalysisFrames: 0,
  },
  analysis: {
    workerState: runtime.running ? 'running' : 'paused',
    analysisSampleRate: config.audio.captureSampleRate,
    windowSize: config.analysis.windowSize,
    hopSize: config.analysis.hopSize,
    frameCount: runtime.frameIndex,
    frameSkipCount: 0,
    avgProcessMs: null,
    p95ProcessMs: null,
    maxProcessMs: null,
    deadlineMissCount: 0,
    lastFrameAgeMs:
      runtime.lastFrameAtMs === null ? null : atMs - runtime.lastFrameAtMs,
  },
  detector: {
    algorithm: config.analysis.algorithm,
    active: runtime.running,
    voicedRatio: 1,
    avgConfidence: 0.95,
    lowConfidenceFrameCount: 0,
    octaveCorrectionCount: 0,
    f0MinHz: config.analysis.minFrequencyHz,
    f0MaxHz: config.analysis.maxFrequencyHz,
  },
  postProcessor: {
    smoothingMode: config.analysis.smoothing,
    heldFrameCount: 0,
    interpolatedFrameCount: 0,
    releasedFrameCount: 0,
    discontinuityCount: 0,
    lastOutputState: 'measured',
  },
  eventDispatcher: {
    eventQueueDepth: 0,
    pitchEventIntervalMs: effectiveEventIntervalMs(config),
    emittedPitchFrameCount: runtime.frameIndex,
    droppedPitchFrameCount: 0,
    emittedErrorCount: 0,
    lastEventAgeMs: null,
  },
  uiBridge: {
    lastSnapshotAgeMs: 0,
    diagnosticsPollingCount: 0,
    diagnosticsDroppedCount: 0,
    lastJsConsumerLagMs: null,
    snapshotStale: false,
  },
  degradation: { level: 0, reason: 'none', sinceMs: null, action: 'none' },
  platform: { platform: 'ios' },
});

type Listeners = Map<
  PitchTrackerEventName,
  Set<(event: PitchTrackerEvent) => void>
>;

const emit = (listeners: Listeners, event: PitchTrackerEvent): void => {
  const set = listeners.get(event.type);
  if (!set) return;
  for (const listener of set) listener(event);
};

/**
 * Create a mock PitchTrackerModule. Emits the deterministic sustained voice as
 * `pitch_frame` events on `output.eventIntervalMs`. `now()` is injectable so tests
 * can drive timestamps without a real clock.
 */
export const createMockPitchTrackerModule = (
  now: () => number = () => Date.now(),
  voice: MockVoice = 'sustain',
): PitchTrackerModule => {
  const listeners: Listeners = new Map();
  let config = createPitchTrackerConfig();
  let timer: ReturnType<typeof setInterval> | null = null;
  let frameIndex = 0;
  let startedAtMs: number | null = null;
  let lastFrameAtMs: number | null = null;

  const stopTimer = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const startTimer = (): void => {
    stopTimer();
    const intervalMs = effectiveEventIntervalMs(config);
    timer = setInterval(() => {
      const atMs = now();
      lastFrameAtMs = atMs;
      emit(listeners, {
        type: 'pitch_frame',
        frame: buildFrame(config, frameIndex, atMs, voice),
      });
      if (
        config.output.includePerformanceMetrics &&
        frameIndex > 0 &&
        frameIndex % METRICS_EVERY_FRAMES === 0
      ) {
        emit(listeners, {
          type: 'performance_metrics',
          metrics: buildMetrics(frameIndex, now()),
        });
      }
      frameIndex += 1;
    }, intervalMs);
  };

  return {
    start: (next: PitchTrackerConfig): Promise<PitchTrackerSessionInfo> => {
      config = next;
      frameIndex = 0;
      lastFrameAtMs = null;
      startedAtMs = now();
      const session: PitchTrackerSessionInfo = {
        sessionId: MOCK_SESSION_ID,
        startedAtMs,
        captureSampleRate: config.audio.captureSampleRate,
        analysisSampleRate: config.audio.captureSampleRate,
        config,
      };
      emit(listeners, { type: 'session_started', session });
      startTimer();
      return Promise.resolve(session);
    },
    stop: (): Promise<void> => {
      stopTimer();
      startedAtMs = null;
      emit(listeners, { type: 'session_stopped', reason: 'user_requested' });
      return Promise.resolve();
    },
    pause: (): Promise<void> => {
      stopTimer();
      return Promise.resolve();
    },
    resume: (): Promise<void> => {
      startTimer();
      return Promise.resolve();
    },
    updateConfig: (patch: Partial<PitchTrackerConfig>): Promise<void> => {
      config = { ...config, ...patch };
      emit(listeners, { type: 'config_changed', config });
      return Promise.resolve();
    },
    getDiagnosticsSnapshot: (): Promise<DiagnosticsSnapshot> =>
      Promise.resolve(
        buildDiagnostics(config, now(), {
          running: timer !== null,
          startedAtMs,
          frameIndex,
          lastFrameAtMs,
        }),
      ),
    addListener: <N extends PitchTrackerEventName>(
      eventName: N,
      listener: (event: PitchTrackerEventOf<N>) => void,
    ): Subscription => {
      const set = listeners.get(eventName) ?? new Set();
      set.add(listener as (event: PitchTrackerEvent) => void);
      listeners.set(eventName, set);
      return {
        remove: () => {
          listeners
            .get(eventName)
            ?.delete(listener as (event: PitchTrackerEvent) => void);
        },
      };
    },
  };
};
